import { useEffect, useRef, type ReactNode } from 'react';
import { toast } from 'sonner';
import { registerAuthBridge } from '@shared/api';
import { authService } from '@shared/services/auth.service';
import { useAuthStore } from '@shared/store/auth.store';
import { useCompanyStore } from '@shared/store/company.store';
import { queryClient } from '@shared/lib/query-client';
import { logger } from '@shared/utils/logger';
import { storage } from '@shared/utils/storage';
import { broadcastSignedOut, onSignedOutElsewhere } from '@shared/auth/session-coordination';
import { restoreSession } from '@shared/auth/restore-session';
import { PATHS } from '@shared/router/paths';

interface AuthProviderProps {
  children: ReactNode;
}

/** Collapse repeat toasts (a refused screen can fire several requests). */
const FORBIDDEN_TOAST_ID = 'api-forbidden';
const RATE_LIMIT_TOAST_ID = 'api-rate-limited';

/** At most one background authorization re-read per this many milliseconds. */
const AUTHORIZATION_REFRESH_INTERVAL_MS = 5_000;
let lastAuthorizationRefresh = 0;
let authorizationRefreshInFlight = false;

/**
 * Re-read `/auth/me` into the auth store, quietly and throttled.
 *
 * Someone else can change this user's access while they are signed in — a
 * role, an application, a Finance grant. The backend applies it on the next
 * request (Finance keys live; other services within seconds). This brings the
 * navigation and the guards into line without a reload. Triggered by a refused
 * request and by the tab regaining focus. Never throws.
 */
function refreshAuthorizationInBackground(): void {
  const store = useAuthStore.getState();
  if (!store.accessToken) return;
  const now = Date.now();
  if (authorizationRefreshInFlight) return;
  if (now - lastAuthorizationRefresh < AUTHORIZATION_REFRESH_INTERVAL_MS) return;
  lastAuthorizationRefresh = now;
  authorizationRefreshInFlight = true;
  authService
    .getCurrentUser()
    .then((user) => {
      if (useAuthStore.getState().user?.id === user.id) useAuthStore.getState().setUser(user);
    })
    .catch((error: unknown) => logger.debug('Background authorization refresh failed', error))
    .finally(() => {
      authorizationRefreshInFlight = false;
    });
}

/** End the local session and go to the sign-in page (session expired or revoked). */
function endLocalSession(reason: 'expired' | 'signed-out-elsewhere'): void {
  const store = useAuthStore.getState();
  const activeId = store.activeAccountId;
  const successor = activeId ? store.forgetAccount(activeId) : null;
  store.clear();
  useCompanyStore.getState().clear();
  queryClient.clear();
  storage.purgeSessionScoped();
  if (window.location.pathname.startsWith(PATHS.auth.login)) return;
  const params = new URLSearchParams();
  params.set('reason', reason);
  if (successor) params.set('switch', '1');
  window.location.assign(`${PATHS.auth.login}?${params.toString()}`);
}

/**
 * Wires the API layer to the auth store and restores the session on load
 * (`restoreSession`): the session handed over by the application switcher
 * takes precedence over the one this application last used.
 * `isInitializing` gates route rendering so guards never flash the login
 * screen or an Access Denied page during the handshake.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const bridgeRegistered = useRef(false);
  if (!bridgeRegistered.current) {
    registerAuthBridge({
      getAccessToken: () => useAuthStore.getState().accessToken,
      getActiveSessionId: () => useAuthStore.getState().activeSessionId,
      onSessionRefreshed: (session) =>
        useAuthStore.getState().setSession(session.user, session, session),
      onSessionExpired: () => endLocalSession('expired'),
      onForbidden: (error) => {
        toast.error(error.message || 'You do not have permission to perform this action.', {
          id: FORBIDDEN_TOAST_ID,
        });
        refreshAuthorizationInBackground();
      },
      onRateLimited: (error) => {
        toast.warning(error.message, { id: RATE_LIMIT_TOAST_ID });
      },
    });
    bridgeRegistered.current = true;
  }

  useEffect(() => {
    let cancelled = false;
    void restoreSession(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, []);

  // Access granted or revoked elsewhere while this tab was in the background.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') refreshAuthorizationInBackground();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, []);

  // Signed out in another tab of this application: follow it.
  useEffect(
    () =>
      onSignedOutElsewhere((sessionId) => {
        const { activeSessionId, isAuthenticated } = useAuthStore.getState();
        if (isAuthenticated && (sessionId === null || sessionId === activeSessionId)) {
          endLocalSession('signed-out-elsewhere');
        }
      }),
    [],
  );

  return children;
}

export { broadcastSignedOut };
